/* eslint-disable react/prop-types */

import { colors } from '@lmc/common/constants'
import { Button, Icon } from '@lmc/ui-kit'
import _ from 'lodash'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { getJSON } from '../../../common/services/api'
import { ProfileLink } from '../../../components/generic'
import { EmptyAvatars, HomeCard } from './CardComponents'

const TITLE = 'Family App'

type Profile = any

type Data = {
  result: {
    activeAccounts: any
    seenRecently: unknown[]
    invited: boolean
  }
}

type Response = {
  data: Data
}

const getBodyText = (seenRecently: number, invited: boolean) => {
  if (!invited)
    return <p>You`re all set up, but no family members have been invited yet</p>
  if (!seenRecently)
    return (
      <p>
        It doesn’t look like any family members have been active recently (last
        24 hrs)
      </p>
    )
  if (seenRecently === 1) {
    return (
      <p>
        <strong>One family member</strong> has been active recently (last 24h)
      </p>
    )
  }
  return (
    <p>
      <strong>{seenRecently} family members</strong> have been active recently
      (last 24h)
    </p>
  )
}

const BodySkeleton = () => (
  <SkeletonWrapper>
    <SkeletonLine $width="200px" />
    <SkeletonLine $width="280px" />
    <EmptyAvatars />
  </SkeletonWrapper>
)

const Body = ({ data }: Response) => {
  const [nShowing, showMore] = useState(10)
  if (!data) return <BodySkeleton />
  return (
    <div>
      <p style={{ marginBlock: 0 }}>
        Active <StyledCount>({data?.result?.activeAccounts || 0})</StyledCount>
        Sent Invite <StyledCount>({data?.result?.invited || 0})</StyledCount>
      </p>
      {getBodyText(data?.result?.seenRecently?.length, data?.result?.invited)}
      <div className="lmc-flex-grid">
        {_.take(data?.result?.seenRecently || [], nShowing).map(
          (profile: Profile) => (
            <ProfileLink
              key={profile.id}
              list="residents"
              id={profile.resident}
              name={profile.name}
              picture={profile.picture}
            />
          )
        )}
        {data?.result?.seenRecently?.length > nShowing ? (
          // eslint-disable-next-line jsx-a11y/anchor-is-valid
          <a className="lmc-more-link" onClick={() => showMore(nShowing + 30)}>
            More...
          </a>
        ) : null}
      </div>
    </div>
  )
}

const FamilyCard = () => {
  const [data, setData] = useState()

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    getJSON({ url: `/api/list/carers/family-status` }).then(setData)
  }, [])

  return (
    <HomeCard
      title={TITLE}
      footer={
        <StyledFooter>
          <Link to="/settings/family-invite">
            <Button variant="primary" icon={<Icon name="MailOutlined" />}>
              Invite a Family Member
            </Button>
          </Link>
        </StyledFooter>
      }
    >
      <Body data={data as unknown as Data} />
    </HomeCard>
  )
}

const StyledFooter = styled.div`
  display: flex;
  justify-content: flex-end;
`

const StyledCount = styled.span`
  margin-right: 12px;
  color: ${colors.darkgrey};
`

const SkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 4px;
`

const SkeletonLine = styled.div<{ $width: string }>`
  width: ${({ $width }) => $width};
  height: 14px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.color.neutral.color8};
`

export default FamilyCard
